"use client";

import React, { useEffect, useRef, useState } from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Scissors } from "lucide-react";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const timelineData = [{
  year: "2010",
  title: "Het Begin",
  description: "Opende onze deuren in Middelharnis met een visie om premium, traditionele verzorging terug te brengen naar de moderne man."
}, {
  year: "2015",
  title: "Uitbreiding van het Ambacht",
  description: "Ons team van meesterbarbiers groeide en we breidden onze diensten uit om te voldoen aan de toenemende vraag naar kwaliteitsfades en baardverzorging."
}, {
  year: "2020",
  title: "Een Decennium van Uitmuntendheid",
  description: "Vierde ons 10-jarig jubileum en bereikte een mijlpaal van meer dan 20.000 tevreden klanten die vertrouwen op onze expertise."
}, {
  year: "Vandaag",
  title: "Modern Erfgoed",
  description: "Blijven de standaard zetten in Goeree-Overflakkee, waarbij we klassieke barbertechnieken combineren met eigentijdse stijlen."
}];
export default function OurJourneySince2010() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: timelineRef,
    isVisible: isTimelineVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: imageRef,
    isVisible: isImageVisible
  } = useRevealOnScroll<HTMLDivElement>();
  return <section id="onze-reis-sinds-2010" className="dark bg-background text-foreground py-24 px-4 md:px-8 lg:px-16 overflow-hidden">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">
        
        <div className="flex flex-col">
          <div ref={headerRef} className={`transition-all duration-1000 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="flex items-center gap-3 mb-4">
              <Scissors className="size-5 text-primary" />
              <span className="text-primary font-semibold tracking-wider uppercase text-sm">
                Opgericht 2010
              </span>
            </div>
            <h2 className="text-4xl md:text-5xl font-serif font-bold mb-6 leading-tight uppercase tracking-wider">
              Een erfenis van <br />
              <span className="text-primary">Vakmanschap</span>
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed mb-8 max-w-xl">
              Wat begon als een passie voor traditioneel barbierschap is uitgegroeid tot de belangrijkste bestemming voor herenverzorging in Middelharnis. Met meer dan <strong className="text-foreground font-semibold">30.000 tevreden klanten</strong> combineren we klassieke technieken met moderne stijlen voor een ongeëvenaarde ervaring.
            </p>
          </div>

          <div ref={timelineRef} className={`mt-8 transition-all duration-1000 delay-300 ease-out ${isTimelineVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="space-y-2">
              {timelineData.map((item, i) => <div key={i} data-index={i} className="flex gap-6 group">
                  <div className="flex flex-col items-center">
                    <div className="size-4 rounded-lg bg-background border-2 border-primary shrink-0 mt-1.5 transition-all duration-300 group-hover:scale-125 group-hover:bg-primary group-hover:shadow-[0_0_12px_rgba(239,68,68,0.5)]" />
                    {i !== timelineData.length - 1 && <div className="w-0.5 h-full bg-border mt-2 group-hover:bg-primary/30 transition-colors duration-300" />}
                  </div>
                  <div className="pb-10">
                    <h3 className="text-2xl font-bold text-primary font-serif tracking-wide">
                      {item.year}
                    </h3>
                    <h4 className="text-xl font-semibold text-foreground mt-1">
                      {item.title}
                    </h4>
                    <p className="text-muted-foreground mt-2 leading-relaxed max-w-md">
                      {item.description}
                    </p>
                  </div>
                </div>)}
            </div>

            <div className="mt-8">
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 hover:shadow-[0_0_15px_rgba(239,68,68,0.4)] hover:scale-[1.02] font-semibold px-8 transition-all duration-300">
                <Link to="https://salonkee.nl/salon/he-kappers" newTab>
                  Plan een afspraak
                </Link>
              </Button>
            </div>
          </div>
        </div>

        <div ref={imageRef} className={`relative h-full min-h-[600px] lg:min-h-[800px] rounded-xl overflow-hidden group transition-all duration-1000 delay-500 ease-out ${isImageVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-12"}`}>
          <picture className="absolute inset-0 w-full h-full">
            <source media="(max-width: 767px)" srcSet="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1577285/img/IMG-20250225-WA0007.jpg" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1577285/img/IMG-20250225-WA0007.jpg-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1577285/img/IMG-20250225-WA0007.jpg-wvc-sizes" />
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1577285/img/IMG-20250225-WA0006.jpg" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1577285/img/IMG-20250225-WA0006.jpg-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1577285/img/IMG-20250225-WA0006.jpg-wvc-sizes" alt="H&E Kappers Interieur en Oprichters" loading="lazy" className="absolute inset-0 w-full h-full object-cover grayscale contrast-125 brightness-75 transition-all duration-700 group-hover:grayscale-0 group-hover:brightness-100" />
          </picture>
          
          <div className="absolute inset-0 border border-primary/30 m-6 rounded-lg pointer-events-none transition-all duration-700 group-hover:border-primary/60 group-hover:m-4" />
          
          <div className="absolute bottom-0 left-0 right-0 p-10 bg-gradient-to-t from-background via-background/90 to-transparent">
            <div className="translate-y-4 opacity-80 transition-all duration-500 group-hover:translate-y-0 group-hover:opacity-100">
              <p className="text-primary font-serif text-3xl font-bold uppercase tracking-widest">H&E Kappers</p>
              <p className="text-foreground/90 mt-2 text-lg">Zandpad 2, Middelharnis</p>
            </div>
          </div>
        </div>

      </div>
    </section>;
}